import type { ComputedRef, MaybeRefOrGetter } from 'vue';

import { defineStore } from 'pinia';
import { computed, ref, toValue } from 'vue';

import type { KkmHandshakeResult } from '@/shared/api';

/**
 * Пауза после подряд идущих неуспешных опросов. Недоступная касса перестаёт получать команды
 * каждые 15 секунд, но обычный интервал возвращается сразу после первого успешного ответа.
 */
const BACKOFF_STEPS_MS = [15_000, 30_000, 60_000, 120_000] as const;

interface KktHealthEntry {
  /** WARN о потолке backoff пишется один раз за серию отказов, а не на каждом тике. */
  backoffCeilingReported: boolean;
  failureStreak: number;
  handshake: KkmHandshakeResult | null;
  isPolling: boolean;
  lastPolledAt: number | null;
}

export interface KktHealthPollRequest {
  /** Команда опроса. Адаптер и шлюз остаются у фичи — store хранит только состояние устройства. */
  execute: () => Promise<KkmHandshakeResult>;
  locationId: string;
  /**
   * Минимальная пауза между фоновыми опросами локации. Тик, пришедший раньше неё, пропускается —
   * так число потребителей не умножает число команд к кассе, а недоступная касса уходит в backoff.
   * Ручное обновление паузу не передаёт: оно проверяет кассу немедленно.
   */
  minIntervalMs?: number;
}

/**
 * Общее на вкладку состояние здоровья ККТ с ключом по локации.
 *
 * Это состояние физического устройства, а не серверные данные, поэтому Pinia здесь уместна:
 * дублировать в неё ответы Apollo по-прежнему нельзя. Несколько потребителей на одной локации
 * (чип в шапке шахматки и форма оплаты) читают один и тот же результат и не заводят свой цикл опроса.
 */
export const useKktHealthStore = defineStore('kktHealth', () => {
  const entries = ref(new Map<string, KktHealthEntry>());
  /** Незавершённые опросы держим вне реактивного состояния: Promise не должен попадать под proxy. */
  const inFlight = new Map<string, Promise<boolean>>();

  const handshakeFor = (locationId: MaybeRefOrGetter<string>): ComputedRef<KkmHandshakeResult | null> =>
    computed(() => entries.value.get(toValue(locationId))?.handshake ?? null);

  const isPollingFor = (locationId: MaybeRefOrGetter<string>): ComputedRef<boolean> =>
    computed(() => entries.value.get(toValue(locationId))?.isPolling === true);

  /** `true` — на локацию легло свежее показание; `false` — тик пропущен как слишком ранний. */
  const poll = (request: KktHealthPollRequest): Promise<boolean> => {
    const pending = inFlight.get(request.locationId);

    if (pending !== undefined) return pending;
    if (isTooSoon(request)) return Promise.resolve(false);

    const run = runPoll(request).finally(() => inFlight.delete(request.locationId));

    inFlight.set(request.locationId, run);

    return run;
  };

  /** Касса выключена на локации: состояние устройства перестаёт существовать, а не становится неизвестным. */
  const forget = (locationId: string): void => {
    entries.value.delete(locationId);
  };

  return { forget, handshakeFor, isPollingFor, poll };

  async function runPoll(request: KktHealthPollRequest): Promise<boolean> {
    patch(request.locationId, { isPolling: true });

    try {
      const handshake = await request.execute();

      // Пауза отсчитывается от завершения цикла: при watchdog в 15 секунд отсчёт от начала дал бы ноль.
      patch(request.locationId, { handshake, lastPolledAt: Date.now() });
      applyPollOutcome(request.locationId, handshake.kind === 'ready');

      return true;
    } finally {
      patch(request.locationId, { isPolling: false });
    }
  }

  function applyPollOutcome(locationId: string, succeeded: boolean): void {
    if (succeeded) {
      patch(locationId, { backoffCeilingReported: false, failureStreak: 0 });
      return;
    }

    const failureStreak = (entries.value.get(locationId)?.failureStreak ?? 0) + 1;
    const reachedCeiling = failureStreak >= BACKOFF_STEPS_MS.length;
    const alreadyReported = entries.value.get(locationId)?.backoffCeilingReported === true;

    if (reachedCeiling && !alreadyReported) {
      console.warn('[cash-shift.kkt-health-backoff-ceiling]', { locationId });
    }

    patch(locationId, { backoffCeilingReported: reachedCeiling || alreadyReported, failureStreak });
  }

  function isTooSoon(request: KktHealthPollRequest): boolean {
    const entry = entries.value.get(request.locationId);

    if (request.minIntervalMs === undefined || entry?.lastPolledAt == null) return false;

    return Date.now() - entry.lastPolledAt < Math.max(request.minIntervalMs, backoffMs(entry.failureStreak));
  }

  function backoffMs(failureStreak: number): number {
    return failureStreak === 0 ? 0 : (BACKOFF_STEPS_MS[Math.min(failureStreak, BACKOFF_STEPS_MS.length) - 1] ?? 0);
  }

  function patch(locationId: string, changes: Partial<KktHealthEntry>): void {
    const current = entries.value.get(locationId) ?? {
      backoffCeilingReported: false,
      failureStreak: 0,
      handshake: null,
      isPolling: false,
      lastPolledAt: null,
    };

    entries.value.set(locationId, { ...current, ...changes });
  }
});
